import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useSizingConfig from "@babackapp/private-api-client/client/use-sizing-config";
import useUpdateSizingConfig from "@babackapp/private-api-client/client/use-update-sizing-config";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import { Loader2, Settings } from "lucide-react";
import { Switch } from "@babackapp/ui/components/switch";
import { toast } from "@babackapp/ui/components/sonner";
import { TranslatableField } from "@babackapp/ui/components/translatable-field";
import { useCallback, useEffect, useState } from "react";
import { MOCK_CONFIG, USE_MOCK_DATA } from "../lib/mock-data";

export function SizingConfigDialog() {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();
  const [open, setOpen] = useState(false);

  const apiResult = useSizingConfig(shop.id);
  const config = USE_MOCK_DATA ? MOCK_CONFIG : apiResult.data;
  const isLoading = USE_MOCK_DATA ? false : apiResult.isLoading;
  const mutation = useUpdateSizingConfig(shop.id);

  const [threshold, setThreshold] = useState("");
  const [minSampleSize, setMinSampleSize] = useState("");
  const [widgetActive, setWidgetActive] = useState(false);
  const [wordingRunsLarge, setWordingRunsLarge] = useState<
    Record<string, string>
  >({});
  const [wordingRunsSmall, setWordingRunsSmall] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (open && config) {
      setThreshold(config.threshold?.toString() ?? "");
      setMinSampleSize(config.minSampleSize?.toString() ?? "");
      setWidgetActive(!!config.widgetActivatedAt);
      const wording = config.wording ?? {};
      const runsLarge = wording.runs_large;
      const runsSmall = wording.runs_small;
      setWordingRunsLarge(
        runsLarge && typeof runsLarge === "object"
          ? (runsLarge as Record<string, string>)
          : {},
      );
      setWordingRunsSmall(
        runsSmall && typeof runsSmall === "object"
          ? (runsSmall as Record<string, string>)
          : {},
      );
    }
  }, [open, config]);

  const locales = shop.preferences?.languages ?? ["en"];
  const defaultLocale = locales[0] ?? "en";

  const handleSave = useCallback(() => {
    const parsedThreshold = parseFloat(threshold);
    const parsedMinSampleSize = parseInt(minSampleSize, 10);

    if (threshold !== "" && !Number.isFinite(parsedThreshold)) {
      toast.error(t("sizing.config.invalidThreshold"));
      return;
    }
    if (minSampleSize !== "" && !Number.isFinite(parsedMinSampleSize)) {
      toast.error(t("sizing.config.invalidMinSampleSize"));
      return;
    }

    setOpen(false);
    mutation.mutate({
      ...(threshold !== "" &&
        Number.isFinite(parsedThreshold) && {
          threshold: parsedThreshold,
        }),
      ...(minSampleSize !== "" &&
        Number.isFinite(parsedMinSampleSize) && {
          minSampleSize: parsedMinSampleSize,
        }),
      activateWidget: widgetActive,
      wording: {
        runs_large: wordingRunsLarge,
        runs_small: wordingRunsSmall,
      },
    });
  }, [
    t,
    mutation,
    threshold,
    minSampleSize,
    widgetActive,
    wordingRunsLarge,
    wordingRunsSmall,
  ]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          aria-label={t("sizing.config.title")}
        >
          <Settings className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("sizing.config.title")}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="threshold">
                  {t("sizing.config.threshold")}
                </Label>
                <Input
                  id="threshold"
                  type="number"
                  min={0}
                  max={1}
                  step={0.01}
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  placeholder="0.5"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="minSampleSize">
                  {t("sizing.config.minSampleSize")}
                </Label>
                <Input
                  id="minSampleSize"
                  type="number"
                  min={1}
                  step={1}
                  value={minSampleSize}
                  onChange={(e) => setMinSampleSize(e.target.value)}
                  placeholder="15"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="activateWidget"
                checked={widgetActive}
                onCheckedChange={setWidgetActive}
              />
              <Label htmlFor="activateWidget">
                {t("sizing.config.activateWidget")}
              </Label>
            </div>

            <div className="space-y-4">
              <Label>{t("sizing.config.wording")}</Label>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <p className="text-sm text-muted-foreground">
                    {t("sizing.config.wordingRunsLarge")}
                  </p>
                  <TranslatableField
                    value={wordingRunsLarge}
                    onChange={setWordingRunsLarge}
                    locales={locales}
                    defaultLocale={defaultLocale}
                    placeholder={t("sizing.runsLarge")}
                  />
                </div>
                <div className="space-y-1.5">
                  <p className="text-sm text-muted-foreground">
                    {t("sizing.config.wordingRunsSmall")}
                  </p>
                  <TranslatableField
                    value={wordingRunsSmall}
                    onChange={setWordingRunsSmall}
                    locales={locales}
                    defaultLocale={defaultLocale}
                    placeholder={t("sizing.runsSmall")}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={mutation.isPending || isLoading}
              >
                {mutation.isPending
                  ? t("misc.loading")
                  : t("sizing.config.save")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
